import { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { DIRECTION, SEARCH_BOUND_MINIMUM_LENGTH_ACCOUNT, TRANSACTION_TYPES, TRANSFER_TYPE } from 'components/constants';
import i18nContext from 'components/i18n-context';
import CustomAsyncSelect from 'components/CustomAsyncSelect';
import { getTransactionsListRequest } from 'services/requestAgent';
import './RepeatTransactionAsyncSelect.scss';

// Same rule as `actions.repeat` on the backend: any OUTGOING NORMAL transaction, in any status
const REPEATABLE_TRANSACTIONS_FILTERS = {
  types: [TRANSACTION_TYPES.OUTGOING],
  transfer_types: [TRANSFER_TYPE.NORMAL]
};

const RepeatTransactionAsyncSelect = ({ value, transactionNumber, onChange, error }) => {
  const i18n = useContext(i18nContext);

  const getValueLabel = (transaction) =>
    transaction
      ? [
          transaction.transactionNumber,
          transaction.targetAmount !== null && transaction.targetAmount !== undefined
            ? `${transaction.targetAmount} ${transaction.currency}`
            : null,
          transaction.recipient?.name,
          transaction.status && i18n.getMessage(`transactionStatus.${transaction.status}`)
        ]
          .filter(Boolean)
          .join(' · ')
      : '';

  const noOptionsMessage = () => i18n.getMessage('createTransaction.repeat.transactionSelect.noTransactionsFound');
  const loadingMessage = ({ inputValue }) =>
    inputValue.trim()?.length < SEARCH_BOUND_MINIMUM_LENGTH_ACCOUNT
      ? i18n.getMessage('createTransaction.repeat.transactionSelect.searchWillStartWith') +
        SEARCH_BOUND_MINIMUM_LENGTH_ACCOUNT +
        i18n.getMessage('createTransaction.repeat.transactionSelect.characters')
      : i18n.getMessage('createTransaction.repeat.transactionSelect.searching');

  const loadOptions = async (searchText) => {
    let response = null;
    if (searchText?.length >= SEARCH_BOUND_MINIMUM_LENGTH_ACCOUNT) {
      response = await getTransactionsListRequest(
        { page: 0, size: 10 },
        'created_at',
        DIRECTION.DESC,
        searchText,
        REPEATABLE_TRANSACTIONS_FILTERS
      );
    }

    return response
      ? response.content.map((transaction) => ({
          value: transaction,
          label: getValueLabel(transaction)
        }))
      : [];
  };

  // Opened from the transaction details: preselect the transaction passed in the URL
  useEffect(() => {
    if (!value && transactionNumber) {
      loadOptions(transactionNumber).then((options) => {
        const option = options.find((item) => item.value.transactionNumber === transactionNumber);
        if (option) {
          onChange(option.value);
        }
      });
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionNumber]);

  return (
    <div className={'repeat-transaction-async-select-wrapper'}>
      <CustomAsyncSelect
        isRequired
        isAutoFocus={!transactionNumber}
        className={'repeat-transaction-async-select-input'}
        onChange={(option) => onChange(option.value)}
        loadList={loadOptions}
        handleInputChange={() => {}}
        name={'originalTransaction'}
        valueLabel={getValueLabel(value)}
        noOptionsMessage={noOptionsMessage}
        loadingMessage={loadingMessage}
        label={i18n.getMessage('createTransaction.repeat.transactionSelect.label')}
        placeholder={i18n.getMessage('createTransaction.repeat.transactionSelect.placeholder')}
        error={error}
      />
    </div>
  );
};

RepeatTransactionAsyncSelect.propTypes = {
  value: PropTypes.object,
  transactionNumber: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string
};

export default RepeatTransactionAsyncSelect;
